import { createClient } from '@supabase/supabase-js'
import type { Database } from '../supabase-types'
// For comprehensive types with better documentation, see: ../../types/database.ts

// Environment variable validation
const metaEnv = (typeof import.meta !== 'undefined') ? (import.meta as any).env : undefined
function getRuntimeOverride() {
  try {
    if (typeof window === 'undefined') return { url: undefined as string | undefined, key: undefined as string | undefined }
    const sp = new URLSearchParams(window.location.search)
    const urlQ = sp.get('supabaseUrl') || sp.get('SUPABASE_URL')
    const keyQ = sp.get('supabaseAnonKey') || sp.get('SUPABASE_ANON_KEY')
    if (urlQ) localStorage.setItem('fw_supabase_url', urlQ)
    if (keyQ) localStorage.setItem('fw_supabase_anon', keyQ)
    const urlLS = localStorage.getItem('fw_supabase_url') || undefined
    const keyLS = localStorage.getItem('fw_supabase_anon') || undefined
    const urlWin = (window as any).__FW_SUPABASE_URL as string | undefined
    const keyWin = (window as any).__FW_SUPABASE_ANON_KEY as string | undefined
    return { url: urlLS || urlWin, key: keyLS || keyWin }
  } catch { return { url: undefined as string | undefined, key: undefined as string | undefined } }
}
const runtime = getRuntimeOverride()
const supabaseUrl = runtime.url || (metaEnv && metaEnv.VITE_SUPABASE_URL) || (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_URL : undefined)
const supabaseAnonKey = runtime.key || (metaEnv && metaEnv.VITE_SUPABASE_ANON_KEY) || (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_ANON_KEY : undefined)

if (!supabaseUrl) {
  throw new Error('Missing env var: VITE_SUPABASE_URL')
}

if (!supabaseAnonKey) {
  throw new Error('Missing env var: VITE_SUPABASE_ANON_KEY')
}

// Create typed Supabase client
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    debug: false  // Disable verbose auth logs
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Synchronous bootstrap: seed Realtime with the persisted JWT before any
// React component mounts. supabase-js v2 only pushes the user JWT to
// Realtime via async events (INITIAL_SESSION, heartbeat callback) — but
// feature hooks like useNotificationListener run their useEffect on first
// paint and call `supabase.channel(...).subscribe()` immediately. Without
// this seed, the resulting WebSocket join goes out with the bootstrap
// anon JWT, and the realtime.subscription_check_filters trigger rejects
// any filter on a column anon lacks SELECT on (e.g. school_id).
//
// Default storage key in supabase-js is `sb-${baseUrl.hostname.split('.')[0]}-auth-token`
// (see SupabaseClient.js). Reads it directly from localStorage so we
// don't have to await auth.getSession().
;(function seedRealtimeAuthFromStorage() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return
  try {
    const host = new URL(supabaseUrl).hostname.split('.')[0]
    const key = `sb-${host}-auth-token`
    const raw = localStorage.getItem(key)
    if (!raw) return
    const parsed = JSON.parse(raw) as
      | { access_token?: string; currentSession?: { access_token?: string } }
      | null
    const token =
      parsed?.access_token || parsed?.currentSession?.access_token
    if (token) {
      // Fire-and-forget — setAuth is async but channels created after this
      // tick will pick up the new token.
      void supabase.realtime.setAuth(token)
    }
  } catch {
    // best-effort; if this fails the AuthContext fallback still kicks in
  }
})()

// Helper types
export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row']
export type Enums<T extends keyof Database['public']['Enums']> = Database['public']['Enums'][T]

// Common table types
export type UserProfile = Tables<'user_profiles'>
export type CourseLesson = Tables<'course_lessons'>
export type StudentAttendanceLog = Tables<'student_attendance_logs'>

// Authentication helpers
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser()
  if (error) throw error
  return user
}

export async function getCurrentSession() {
  const { data: { session }, error } = await supabase.auth.getSession()
  if (error) throw error
  return session
}

// Profile helpers
export async function getCurrentUserProfile() {
  const user = await getCurrentUser()
  if (!user) return null

  // Prefer profile_id from metadata or auth uid, staging-safe
  const pid = (user.user_metadata as any)?.profile_id || user.id
  // Try user_profiles by id
  const { data: up } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', pid)
    .maybeSingle()
  if (up) return up as any
  // Fallback to minimal view
  const { data: vmin } = await supabase
    .from('vw_react_user_profiles_min' as any)
    .select('id, school_id, first_name, last_name, role_id, role_name')
    .eq('id', pid)
    .maybeSingle()
  return vmin as any
}

// Authentication functions
export async function handleLogin(email: string, password: string) {
  try {
    console.log('🔐 Attempting login for:', email)

    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: email,
      password: password
    })

    if (error) {
      console.error('❌ Auth error:', error)
      throw error
    }

    console.log('✅ Authentication successful for:', authData.user.email)

    return {
      user: authData.user
      // Note: profile and role are handled by App component
    }

  } catch (error) {
    console.error('💥 Login error:', error)
    throw error
  }
}

export async function handleLogout() {
  try {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    console.log('✅ Logout successful')
  } catch (error) {
    console.error('💥 Logout error:', error)
    throw error
  }
}

// Role-based routing helper
export function getRouteByRole(role: string): string {
  const routes: Record<string, string> = {
    'Parent': '/dashboard/parent',
    'Teacher': '/dashboard/teacher',
    'Admin': '/dashboard/admin',
    'Student': '/dashboard/student',
    'Erzieher*innen': '/dashboard/teacher',
    'Externe': '/dashboard/external',
    'Super Admin': '/dashboard/admin'
  }

  return routes[role] || '/dashboard/parent'
}
